/** Parse YYYY-MM-DD, MM/DD/YYYY, or ISO date strings to Date. */
export function parseDate(dateStr: string): Date {
  if (!dateStr) return new Date(0);
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
    const [y, m, d] = dateStr.split("-").map(Number) as [number, number, number];
    return new Date(y, m - 1, d);
  }
  return new Date(dateStr);
}

/** Format a date string as "Jan 15". */
export function fmtDateShort(dateStr: string): string {
  const d = parseDate(dateStr);
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

/**
 * Format a Date as YYYY-MM-DD in the **local** calendar.
 *
 * Not `toISOString()`. That converts to UTC first, so a Date built from
 * local components lands on the wrong day whenever the local offset
 * pushes it across midnight UTC: 8pm on 30 September in Toronto is
 * already 1 October in UTC and would render as "2026-10-01". Every date
 * input this fills defaults to a value the user did not pick, and on a
 * month's last evening an entry is filed into the next month.
 */
export function toDateInput(d: Date): string {
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

/** Parse a "YYYY-MM" string into { year, month } (1-indexed). */
export function parseMonth(ym: string): { year: number; month: number } {
  const [y, m] = ym.split("-").map(Number) as [number, number];
  return { year: y, month: m };
}

/** Format a Date as "YYYY-MM". */
export function formatMonth(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

/**
 * Count the calendar months a range touches, inclusive.
 *
 * This is the rule the generated invoice uses, so it is the rule
 * everything else must use. A range of Jan 31 to Feb 1 counts as two
 * months, because a monthly fee is owed for both.
 */
export function monthsBetweenDates(start: Date, end: Date): number {
  return (
    (end.getFullYear() - start.getFullYear()) * 12 +
    (end.getMonth() - start.getMonth()) +
    1
  );
}

/**
 * Count the quarters a range touches, rounding a started quarter up.
 *
 * A quarterly fee recurs once every three months, so a six-month range
 * owes it twice. A range shorter than a quarter still owes it once —
 * the fee is not pro-rated.
 */
export function quartersBetweenDates(start: Date, end: Date): number {
  return Math.ceil(monthsBetweenDates(start, end) / 3);
}

/** String-input wrapper over {@link monthsBetweenDates}. */
export function monthsBetween(startStr: string, endStr: string): number {
  return monthsBetweenDates(parseDate(startStr), parseDate(endStr));
}

export interface DateRangePreset {
  label: string;
  start: string;
  end: string;
}

/** Return named date range presets relative to today. */
export function getDateRangePresets(today: Date = new Date()): Record<string, DateRangePreset> {
  const y = today.getFullYear();
  const m = today.getMonth();

  const firstOfMonth = new Date(y, m, 1);
  const lastOfMonth = new Date(y, m + 1, 0);
  const firstOfLastMonth = new Date(y, m - 1, 1);
  const lastOfLastMonth = new Date(y, m, 0);
  const threeMonthsAgo = new Date(y, m - 2, 1);

  return {
    "this-month": {
      label: "This Month",
      start: toDateInput(firstOfMonth),
      end: toDateInput(lastOfMonth),
    },
    "last-month": {
      label: "Last Month",
      start: toDateInput(firstOfLastMonth),
      end: toDateInput(lastOfLastMonth),
    },
    "last-3-months": {
      label: "Last 3 Months",
      start: toDateInput(threeMonthsAgo),
      end: toDateInput(lastOfMonth),
    },
    ytd: {
      label: "Year to Date",
      start: `${y}-01-01`,
      end: toDateInput(today),
    },
    "last-year": {
      label: "Last Year",
      start: `${y - 1}-01-01`,
      end: `${y - 1}-12-31`,
    },
  };
}
